import { supportedLocaleCodes } from '~/features/internationalization/languages';

/**
 * Route prefixes considered marketing surfaces, where the OneTrust
 * cookie-consent banner should load. Every other route is treated as a
 * non-marketing / product surface and will not load the banner.
 *
 * Intentionally an allowlist: the project has grown many product surfaces, and
 * new product pages default to "no banner" without anyone having to remember
 * to add them here. The trade-off is that new marketing pages MUST be added
 * here or they will silently ship without the consent banner — review this
 * list whenever a new marketing route ships.
 *
 * Matching is segment-based against the first path segment (after any locale
 * and CF Worker experiment prefixes are stripped). Add entries as the first
 * segment of the marketing route (no leading slash).
 */
const MARKETING_ROUTE_PREFIXES = [
  '',
  'about-us',
  'ai',
  'become-a-mentor',
  'career-outcomes',
  'catalog',
  'certificate',
  'checkout',
  'contact',
  'course',
  'course-collection',
  'ebook',
  'enterprise',
  'event',
  'experience',
  'free-trial',
  'get-personalized-discount',
  'get-reimbursed',
  'get-started',
  'government',
  'infographic',
  'legal',
  'masters-artificial-intelligence',
  'mba-ai-product-management',
  'microsoft-ai-skills-fest',
  'news',
  'partnerships',
  'personalized-discount',
  'plans',
  'preregister',
  'quick-checkout',
  'recognition-of-prior-learning',
  'report',
  'resource-center',
  'scholarships',
  'school',
  'schools',
  'tech-requirements',
  'topic',
  'video',
  'webinar',
] as const;

const MARKETING_PREFIX_SET: ReadonlySet<string> = new Set(MARKETING_ROUTE_PREFIXES);

/**
 * Non-default locale codes used as URL prefixes by Next.js i18n routing (see
 * `locale-codes.json` / `next.config.js`). The default locale (`'default'`)
 * gets no URL prefix, so only these codes appear as a leading path segment.
 * A localized marketing URL like `/de-DE/catalog` must be unwrapped to
 * `/catalog` before the marketing-prefix check or it would wrongly be treated
 * as a non-marketing surface.
 */
const LOCALE_PREFIX_SET: ReadonlySet<string> = new Set(supportedLocaleCodes);

/**
 * First path segment of CF Worker experiment / flag variant routes, per
 * `getExperimentUrl` in `src/cf-worker/utils.ts`. Variants are served at
 * `/{EXPERIMENT_ROUTE_PREFIX}/{experimentKey}/{variationKey}/{originalPath}`.
 * The worker reverse-proxies the rewritten path to Next, so `_document` sees
 * the prefixed URL — we strip the prefix to recover the underlying surface.
 */
const EXPERIMENT_ROUTE_PREFIXES = new Set(['test', 'flag']);
const EXPERIMENT_PREFIX_SEGMENT_COUNT = 3;

/**
 * Returns true when the given pathname is a marketing surface where the
 * OneTrust cookie-consent banner should appear. Pure route check only — does
 * not consider whether OneTrust is enabled for the current environment. The
 * env-level kill switch (`NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT === 'false'`) is
 * evaluated in `_document.tsx` so this stays unit-testable.
 *
 * Only evaluated on the initial server render (full page load); it does not
 * re-run on client-side navigation. CF Worker route-swap experiment / flag
 * variants (`/test/...` `/flag/...`) are unwrapped to their underlying surface
 * before matching, so experiment variants of marketing pages still get the
 * banner. Non-default i18n locale prefixes (e.g. `/de-DE/catalog`) are also
 * stripped so localized marketing pages load the banner. `useClientExperiment`
 * UI toggles don't change the URL and are already covered by the base check.
 */
export function isMarketingRoute(pathname: string): boolean {
  const segments = toPathSegments(pathname);
  if (segments === null) {
    return false;
  }

  const surfaceSegments = stripLocalePrefix(unwrapExperimentPrefix(segments));
  return isMarketingSurface(surfaceSegments);
}

function isMarketingSurface(segments: string[]): boolean {
  // Root path is the homepage, which is marketing (empty-string prefix).
  if (segments.length === 0) {
    return MARKETING_PREFIX_SET.has('');
  }

  return MARKETING_PREFIX_SET.has(segments[0]);
}

/**
 * Drops a leading non-default locale segment (e.g. `de-DE` in `/de-DE/catalog`,
 * or the bare localized homepage `/de-DE`) so the underlying surface is
 * matched. Applied after experiment-prefix unwrapping so locale prefixes that
 * follow an experiment prefix (`/test/exp/var/de-DE/catalog`) are also handled.
 * The default locale has no URL prefix and is therefore not in
 * `LOCALE_PREFIX_SET`, so unprefixed paths pass through unchanged.
 */
function stripLocalePrefix(segments: string[]): string[] {
  if (segments.length >= 1 && LOCALE_PREFIX_SET.has(segments[0])) {
    return segments.slice(1);
  }

  return segments;
}

/**
 * If the path is a CF Worker experiment/flag variant, drops the
 * `/{rootDir}/{experimentKey}/{variationKey}` prefix and returns the
 * remaining segments (the original surface). Otherwise returns the segments
 * unchanged. A 3-segment variant path (`/test/exp/var`) represents the
 * homepage variant and resolves to an empty segment list.
 */
function unwrapExperimentPrefix(segments: string[]): string[] {
  if (segments.length < EXPERIMENT_PREFIX_SEGMENT_COUNT || !EXPERIMENT_ROUTE_PREFIXES.has(segments[0])) {
    return segments;
  }

  return segments.slice(EXPERIMENT_PREFIX_SEGMENT_COUNT);
}

function toPathSegments(pathname: string): string[] | null {
  if (!pathname) {
    return null;
  }

  try {
    const parsed = new URL(pathname, 'http://localhost');
    return parsed.pathname.split('/').filter((segment) => segment.length > 0);
  } catch {
    return null;
  }
}
